import React, { useState, useEffect } from 'react';
import { Input } from '../../ui/input';
import { useLazyCommand } from '@soap/modules';
import { Loader2, Edit2 } from 'lucide-react';

interface InlinePointsEditorProps {
  pointsGiven: number | null | undefined;
  maxPoints: number;
  assessmentResultId: string;
  questionId: string | null | undefined;
  isEditable: boolean;
  wasSkipped?: boolean;
  onUpdate?: (newPoints: number) => void;
}

export function InlinePointsEditor({
  pointsGiven,
  maxPoints,
  assessmentResultId,
  questionId,
  isEditable,
  wasSkipped = false,
  onUpdate
}: InlinePointsEditorProps) {
  const safePoints = pointsGiven ?? 0;
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(safePoints.toString());
  const [isSaving, setIsSaving] = useState(false);
  const [markQuestionTrigger] = useLazyCommand("C220v1_MarkQuestion");

  useEffect(() => {
    setEditValue(safePoints.toString());
  }, [safePoints]);

  const handleSave = async () => {
    const numValue = Number(editValue);
    if (numValue >= 0 && numValue <= maxPoints && !isNaN(numValue) && questionId) {
      setIsSaving(true);
      try {
        // Create EnumerationAndFlags object for C220 command
        // Format: { SelectedKeys: [string], AllEnumerations: [{ Key: string, Value: string }] }
        const allEnumerations = Array.from({ length: maxPoints + 1 }, (_, i) => ({
          Key: i.toString(),
          Value: i.toString()
        }));

        const scoreEnum = {
          SelectedKeys: [numValue.toString()],
          AllEnumerations: allEnumerations
        };

        await markQuestionTrigger({
          $type: "C220v1_MarkQuestion",
          C220_AssessmentResultId: assessmentResultId,
          C220_QuestionId: questionId,
          C220_Score: scoreEnum
        });

        if (onUpdate) {
          onUpdate(numValue);
        }
        setEditing(false);
      } catch (error) {
        console.error('Failed to update points:', error);
        // Reset to original value on error
        setEditValue(safePoints.toString());
      } finally {
        setIsSaving(false);
      }
    } else {
      // Reset to original value if invalid
      setEditValue(safePoints.toString());
      setEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(safePoints.toString());
      setEditing(false);
    }
  };

  const numValue = Number(editValue);
  const isValid = !isNaN(numValue) && numValue >= 0 && numValue <= maxPoints;
  const actuallyEditable = isEditable && !wasSkipped;

  if (!actuallyEditable) {
    return (
      <span className="text-gray-900 font-semibold">
        {safePoints} / {maxPoints}
      </span>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={maxPoints}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className={`w-20 h-7 text-sm ${!isValid ? 'border-destructive-action focus-visible:ring-destructive-action focus-visible:ring-2' : ''}`}
          autoFocus
        />
        <span className="text-sm text-gray-500">/ {maxPoints}</span>
        {isSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="flex items-center gap-1.5 px-2 py-1 hover:bg-action-blue/10 hover:border-action-blue/40 border border-transparent rounded text-sm font-semibold text-gray-900 transition-colors group"
      disabled={isSaving}
      title="Click to edit score"
    >
      <span>{safePoints} / {maxPoints}</span>
      <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-action-blue transition-colors" />
    </button>
  );
}

